/**
 * BattleResult
 *
 * Body for the result modal opened from BattleScreen on `fun_session_end`.
 * Visual design ported from NSCL's BattleResultModal
 * (nscl/src/app/components/battle/BattleResultModal.tsx) so the two products
 * feel like one family.
 *
 * Layout:
 *   - Win/lose battle icon (trophy / sad face)
 *   - Outcome banner ("🎉 Congratulations! You Win." / "😔 Better luck …")
 *   - Side-by-side score cards with an avatar floating above each, a VS disc
 *     between them, "{n} Right" badges, and an accuracy footnote.
 *   - REMATCH (yellow with bottom-shadow lip) and outlined secondary buttons.
 *
 * No XP in v1 — the persistence layer writes results but we don't award
 * points. (NSCL's +5 XP pill is intentionally not shown here.)
 *
 * The wrapping `<SlideModal>` (BattleScreen) supplies the close affordance
 * and overlay backdrop.
 */

import React, {useEffect, useRef, useState} from 'react';
import {Animated, Image, Pressable, StyleSheet, View} from 'react-native';

import Box from '@/Atoms/Box';
import CCText from '@/Atoms/CCText';
import CCColors from '@/Utils/CCColors';
import {FontSizes} from '@/Utils/Dimensions';

const TEAL = '#3DAB9E';
const RED = '#EB5757';
const DRAW_YELLOW = '#FACF47';

interface Props {
  outcome: 'win' | 'loss' | 'draw';
  myCorrect: number;
  myWrong: number;
  opponentCorrect: number;
  opponentWrong: number;
  myName?: string;
  opponentName?: string;
  myAvatarUrl?: string;
  opponentAvatarUrl?: string;
  reason: 'time_up' | 'forfeit' | 'mistake_out' | 'target_reached';
  /** PTB mistake-limit. Drives the "N mistakes — …" banner copy. */
  maxWrong?: number;
  /** PDB target-correct. Drives the "Solved N first" banner copy. */
  targetCorrect?: number;
  onRematch: () => void;
  onChallengeOther: () => void;
}

export default function BattleResult(props: Props) {
  const {
    outcome,
    myCorrect,
    myWrong,
    opponentCorrect,
    opponentWrong,
    myName,
    opponentName,
    myAvatarUrl,
    opponentAvatarUrl,
    reason,
    maxWrong,
    targetCorrect,
    onRematch,
    onChallengeOther,
  } = props;

  // Card pop-in animation matches NSCL (spring tension 60 / friction 8).
  const scaleAnim = useRef(new Animated.Value(0.85)).current;
  useEffect(() => {
    scaleAnim.setValue(0.85);
    Animated.spring(scaleAnim, {
      toValue: 1,
      tension: 60,
      friction: 8,
      useNativeDriver: true,
    }).start();
  }, [scaleAnim]);

  const isForfeit = reason === 'forfeit';
  const isMistakeOut = reason === 'mistake_out';
  const isTargetReached = reason === 'target_reached';
  const mistakeLabel = `${maxWrong ?? 5} mistakes`;
  const targetLabel = `${targetCorrect ?? 15}`;
  const banner =
    outcome === 'win'
      ? {
          text: isForfeit
            ? '🎉 You Won! Opponent forfeited.'
            : isMistakeOut
            ? `🎉 You Won! Opponent hit ${mistakeLabel}.`
            : isTargetReached
            ? `🏆 You Won! Solved ${targetLabel} first.`
            : '🎉 Congratulations! You Won.',
          color: TEAL,
          sub: isForfeit ? 'Well played!' : 'Amazing work champion!',
          icon: require('@/Assets/Images/Svgs/winBattleIcon.svg'),
        }
      : outcome === 'loss'
      ? {
          text: isForfeit
            ? '😔 You Lost. You forfeited.'
            : isMistakeOut
            ? `😔 You Lost. You hit ${mistakeLabel}.`
            : isTargetReached
            ? `😔 You Lost. Opponent solved ${targetLabel} first.`
            : '😔 You Lost. Better luck next time!',
          color: RED,
          sub: 'Good game!',
          icon: require('@/Assets/Images/Svgs/loseBattleIcon.svg'),
        }
      : {
          text: "It's a Draw!",
          color: DRAW_YELLOW,
          sub: 'Good game!',
          icon: require('@/Assets/Images/Svgs/winBattleIcon.svg'),
        };

  return (
    <Animated.View style={{transform: [{scale: scaleAnim}]}}>
      <Box style={styles.root}>
        <Image source={banner.icon} style={styles.resultIcon} />

        <CCText style={[styles.outcomeText, {color: banner.color}]}>
          {banner.text}
        </CCText>
        <CCText style={styles.subText}>{banner.sub}</CCText>

        <View style={styles.scoreSection}>
          <ScoreCard
            label={myName || 'You'}
            correct={myCorrect}
            wrong={myWrong}
            tag="YOU"
            imageUri={myAvatarUrl}
          />
          <View style={styles.vsContainer}>
            <CCText style={styles.vsText}>VS</CCText>
          </View>
          <ScoreCard
            label={opponentName || 'Opponent'}
            correct={opponentCorrect}
            wrong={opponentWrong}
            imageUri={opponentAvatarUrl}
          />
        </View>

        <View style={styles.buttonGroup}>
          <Pressable
            onPress={onRematch}
            style={({pressed}) => [
              styles.primaryBtn,
              pressed && styles.primaryBtnPressed,
            ]}>
            <CCText style={styles.primaryBtnText}>REMATCH</CCText>
            <CCText style={styles.primaryBtnArrow}>  ⟳</CCText>
          </Pressable>
          <Pressable onPress={onChallengeOther} style={styles.secondaryBtn}>
            <CCText style={styles.secondaryBtnText}>
              Challenge Other Player
            </CCText>
          </Pressable>
        </View>
      </Box>
    </Animated.View>
  );
}

function ScoreCard({
  label,
  correct,
  wrong,
  tag,
  imageUri,
}: {
  label: string;
  correct: number;
  wrong: number;
  tag?: string;
  imageUri?: string;
}) {
  const [imgErr, setImgErr] = useState(false);
  useEffect(() => {
    setImgErr(false);
  }, [imageUri]);
  const showPhoto = Boolean(imageUri?.trim()) && !imgErr;
  const total = correct + wrong;
  const accuracy = total > 0 ? Math.round((correct / total) * 100) : 0;
  const initial = (label[0] ?? 'O').toUpperCase();
  return (
    <View style={styles.scoreCardWrap}>
      <View style={styles.cardAvatar}>
        {showPhoto ? (
          <Image
            source={{uri: imageUri!.trim()}}
            style={styles.cardAvatarImage}
            onError={() => setImgErr(true)}
          />
        ) : (
          <CCText style={styles.cardAvatarText}>{initial}</CCText>
        )}
      </View>
      <View style={styles.scoreCard}>
        <View style={styles.playerLabelRow}>
          <CCText style={styles.playerLabel} numberOfLines={1}>
            {label}
          </CCText>
          {tag && (
            <View style={styles.youTag}>
              <CCText style={styles.youTagText}>{tag}</CCText>
            </View>
          )}
        </View>
        <View style={styles.scoreBox}>
          <CCText style={styles.scoreBoxText}>{correct} Right</CCText>
        </View>
        <View style={styles.scoreBoxWrong}>
          <CCText style={styles.scoreBoxText}>{wrong} Wrong</CCText>
        </View>
        <CCText style={styles.accuracyText}>{accuracy}% accuracy</CCText>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  root: {
    alignItems: 'center',
    width: '100%',
    paddingTop: 4,
    paddingBottom: 4,
  },
  resultIcon: {
    width: 56,
    height: 64,
    marginBottom: 14,
  },
  outcomeText: {
    fontSize: FontSizes[20] || 20,
    fontWeight: '600',
    textAlign: 'center',
    marginBottom: 4,
  },
  subText: {
    fontSize: FontSizes[14] || 14,
    color: '#4D3F37',
    textAlign: 'center',
    marginBottom: 18,
  },
  scoreSection: {
    flexDirection: 'row',
    alignItems: 'center',
    width: '100%',
    marginBottom: 22,
    gap: 8,
  },
  scoreCardWrap: {
    flex: 1,
    alignItems: 'center',
  },
  cardAvatar: {
    width: 56,
    height: 56,
    borderRadius: 28,
    backgroundColor: '#FAF6EB',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1,
    marginBottom: -20,
    borderWidth: 2,
    borderColor: CCColors.White,
    overflow: 'hidden',
  },
  cardAvatarImage: {
    width: 56,
    height: 56,
    borderRadius: 28,
  },
  cardAvatarText: {
    fontWeight: '700',
    fontSize: FontSizes[20] || 20,
    color: '#4D3F37',
  },
  scoreCard: {
    width: '100%',
    backgroundColor: 'rgba(250,207,71,0.32)',
    borderRadius: 10,
    paddingTop: 28,
    paddingBottom: 14,
    paddingHorizontal: 10,
    alignItems: 'center',
    gap: 10,
    minHeight: 110,
  },
  playerLabelRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
  },
  playerLabel: {
    fontWeight: '700',
    fontSize: FontSizes[14] || 14,
    color: '#4D3F37',
    textAlign: 'center',
  },
  youTag: {
    backgroundColor: '#FACF47',
    borderRadius: 999,
    paddingHorizontal: 8,
    paddingVertical: 3,
  },
  youTagText: {
    fontWeight: '700',
    fontSize: 11,
    color: '#262322',
  },
  scoreBox: {
    backgroundColor: TEAL,
    borderRadius: 6,
    paddingHorizontal: 8,
    paddingVertical: 10,
    width: '100%',
    alignItems: 'center',
  },
  // Mild red counterpart to scoreBox — used for the "N Wrong" tile right
  // below the Right tile. Same shape as scoreBox so they stack cleanly.
  scoreBoxWrong: {
    backgroundColor: '#E57373',
    borderRadius: 6,
    paddingHorizontal: 8,
    paddingVertical: 10,
    width: '100%',
    alignItems: 'center',
    marginTop: 4,
  },
  scoreBoxText: {
    fontWeight: '700',
    fontSize: FontSizes[18] || 18,
    color: CCColors.White,
  },
  accuracyText: {
    fontSize: FontSizes[12] || 12,
    color: '#4D3F37',
    marginTop: 2,
  },
  vsContainer: {
    width: 36,
    height: 36,
    borderRadius: 18,
    backgroundColor: '#262322',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  vsText: {
    fontWeight: '700',
    fontSize: 11,
    color: CCColors.White,
    letterSpacing: 0.5,
  },
  buttonGroup: {
    width: '100%',
    gap: 10,
  },
  primaryBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#FACF47',
    borderRadius: 10,
    borderBottomWidth: 4,
    borderColor: '#A9873B',
    paddingVertical: 14,
    paddingHorizontal: 32,
    width: '100%',
  },
  primaryBtnPressed: {
    borderBottomWidth: 2,
    paddingVertical: 16,
  },
  primaryBtnText: {
    fontWeight: '700',
    fontSize: FontSizes[15] || 15,
    color: '#4D3F37',
    letterSpacing: 0.5,
  },
  primaryBtnArrow: {
    fontWeight: '700',
    fontSize: FontSizes[15] || 15,
    color: '#4D3F37',
  },
  secondaryBtn: {
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: CCColors.White,
    borderRadius: 10,
    borderWidth: 1.5,
    borderColor: '#D9D9D9',
    paddingVertical: 12,
    paddingHorizontal: 24,
    width: '100%',
  },
  secondaryBtnText: {
    fontWeight: '500',
    fontSize: FontSizes[14] || 14,
    color: '#4D3F37',
  },
});
